import React from 'react'
import {
  fetchCommunityMentionUsersByIds,
  type CommunityMentionUserProfile,
} from '../services/communityMentionUsersService'

const MENTION_USERS_REFRESH_MS = 2 * 60 * 1000

const mentionUsersCache: Record<string, CommunityMentionUserProfile> = {}
const mentionUsersLastFetchedAt = new Map<string, number>()

function normalizeIds(userIds: string[]): string[] {
  const set = new Set<string>()
  for (const id of userIds || []) {
    const value = String(id || '').trim()
    if (value) set.add(value)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

export function useCommunityMentionUsers(userIds: string[]) {
  const [usersById, setUsersById] = React.useState<
    Record<string, CommunityMentionUserProfile>
  >(() => {
    const initial: Record<string, CommunityMentionUserProfile> = {}
    normalizeIds(userIds).forEach((id) => {
      const cached = mentionUsersCache[id]
      if (cached) initial[id] = cached
    })
    return initial
  })

  const normalizedIds = React.useMemo(() => normalizeIds(userIds), [userIds])

  React.useEffect(() => {
    if (normalizedIds.length === 0) return

    setUsersById((prev) => {
      let changed = false
      const next = { ...prev }
      normalizedIds.forEach((id) => {
        const cached = mentionUsersCache[id]
        if (!cached) return
        if (!next[id]) {
          next[id] = cached
          changed = true
        }
      })
      return changed ? next : prev
    })

    const now = Date.now()
    const toFetch = normalizedIds.filter((id) => {
      const last = mentionUsersLastFetchedAt.get(id) || 0
      return !last || now - last > MENTION_USERS_REFRESH_MS
    })
    if (toFetch.length === 0) return

    let cancelled = false
    toFetch.forEach((id) => mentionUsersLastFetchedAt.set(id, now))

    ;(async () => {
      const rows = await fetchCommunityMentionUsersByIds(toFetch)
      if (cancelled) return
      if (rows.length === 0) return

      rows.forEach((u) => {
        mentionUsersCache[u.id] = u
      })
      setUsersById((prev) => {
        const next = { ...prev }
        rows.forEach((u) => {
          next[u.id] = u
        })
        return next
      })
    })().catch(() => {
      // ignore
    })

    return () => {
      cancelled = true
    }
  }, [normalizedIds])

  return usersById
}
